import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { Suggestions } from './Suggestions';
import { Answer } from './Answer';
import { FollowUpQuestions } from './FollowUpQuestions';
import { NextBestActions } from './NextBestActions';
import { TimelineNavigation } from './TimelineNavigation';
import { PrimarySearch } from './PrimarySearch';
import { FollowUpSearchBar } from './FollowUpSearchBar';
import { Header } from './Header';
import { defaultTextConfig } from './text-config';
import type {
  FeedbackResponse,
  ChatSearchState,
  Suggestion,
  ChatSearchProps,
  AnswerStreamEvent,
  ActionInputs,
  ActionClick,
  SearchQueryAttributes,
  ClientExecutionResult,
  AttachmentType,
  KnowledgeSourceGroup,
} from './types';
import { useChatSearch, ChatSearchProvider } from './ChatSearchContext';
import { buildConversationContext } from './helpers/buildConversationContext';
import {
  useConversationLoader,
  useScrollManager,
  useSubscriptionManager,
  useIncludePage,
  useFileAttachment,
  useToast,
} from './hooks';
import { useConversationHistory } from './hooks/useConversationHistory';
import { ToastContainer } from './ToastContainer';
import { executeClientAction } from './helpers/excuteClientAction';
import { cancelStreamReader } from './helpers/cancelStreamReader';
import {
  appendNewAnswer,
  setAnswerError,
  setUserFeedback,
  setFollowUpQuestions,
  appendAnswerChunk,
  clearAll,
  clearUserFeedback,
  completeAnswer,
  invokeAction,
  setSearchResults,
  markAsUncertain,
  getActionInputs,
  addActionButtons,
  noArticlesFound,
  setAwaitingKnowledgeGroup,
  setKnowledgeGroupSelected,
  setStatusMessage,
} from './hooks/useAnswerList';
import { LoadingConversation } from './LoadingConversation';
import { MemoizedReactMarkdown } from '../markdown';
import { HistoryPage } from './HistoryPage';
import { ResolvedConversationFooter } from './ResolvedConversationFooter';

import { FeatureFlagProvider, useBooleanFlagValue } from '@/lib/feature-flags';
import { cn, isAnswerUncertain } from '@/lib/utils';
import { useAutocomplete } from '@/lib/hooks';
import {
  searchApi,
  sendFeedback,
  createConversationId,
  fetchFollowUpQuestions,
  fetchAnswerStream,
  sendFeedbackReason,
} from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MemoizedIcon } from '@/components/ui/icon';
import { Features } from '@/lib/features';

export interface ChatSearchHandle {
  createNewThread: (firstQuery?: string) => void;
  showFollowUpThread: (show: boolean) => void;
  subscribeToStateChanges: (callback: (state: ChatSearchState) => void) => () => void;
  focusSearchField: () => void;
  showCloseButton: (showButton: boolean) => void;
  loadConversation: (conversationId: string) => void;
}

// Memoized props for MemoizedReactMarkdown to prevent unnecessary re-renders
const markdownRemarkPlugins = [remarkGfm];
const markdownRehypePlugins = [rehypeRaw];
const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
};

const ChatSearchComponent = forwardRef<ChatSearchHandle, ChatSearchProps>(
  (
    {
      suggestions: initialSuggestions = [],
      collections = [],
      selectedCollectionId,
      onCollectionChange,
      selectedRegion,
      onRegionChange,
      apiKey,
      fetchRegionConfig,
      searchEndpoint,
      answerStreamEndpoint,
      uploadEndpoint,
      feedbackEndpoint,
      feedbackReasonEndpoint,
      loadConversationEndpoint,
      autocompleteEndpoint,
      disableFollowUpQuestions = false,
      enableFileAttachment: enableFileAttachmentProp = false,
      disableFeedback = false,
      disclaimer,
      offset = 0,
      headers,
      textConfig = {},
      followUpQuestionsEndpoint,
      className,
      onToggleSizeButtonClick,
      trackEvent,
      onCloseWidget,
      isWidgetMode = true,
      nextBestActions,
      bodyActionButtons,
      onNextBestActionClick,
      userData,
      redirectRules,
      secretAttributes,
      allowedRegions,
      style,
      isSearchWidget = false,
      isAgentAssist = false,
      hideCitations = false,
      conversationInlineSlot,
      liveHumanAgentChatSlot,
      reconnectHandoff,
      resetHandoff,
      conversationHistoryEndpoint,
      markConversationReadEndpoint,
      markConversationCompletedEndpoint,
      knowledgeSourceGroups,
    },
    ref,
  ) => {
    const {
      answers,
      answerListDispatch,
      query,
      setQuery,
      followUpQuery,
      setFollowUpQuery,
      isSearching,
      setIsSearching,
      showResults,
      setShowResults,
      activeAnswerIndex,
      setActiveAnswerIndex,
      conversationId: currentConversationId,
      setConversationId: setCurrentConversationId,
      clearConversationId: clearCurrentConversationId,
      isLoadingConversation,
      setShowMobileCloseWidgetButton,
    } = useChatSearch(); // Use the context

    const mergedTextConfig = { ...defaultTextConfig, ...textConfig };
    const enableFileAttachmentFlag = useBooleanFlagValue('enableFileAttachment', false);
    const enableFileAttachment = enableFileAttachmentProp || enableFileAttachmentFlag;
    const isHistoryTabEnabled = useBooleanFlagValue(Features.HistoryTab, false);

    const { toasts, showToast, dismissToast } = useToast();

    const getOrCreateConversationId = useCallback(() => {
      let conversationId = currentConversationId;
      if (!conversationId) {
        conversationId = createConversationId();
        setCurrentConversationId(conversationId);
      }

      return conversationId;
    }, [currentConversationId, setCurrentConversationId]);

    const { attachedFiles, isUploading, addFiles, removeFile, clearAttachedFiles, getUploadedAttachments } =
      useFileAttachment({
        uploadEndpoint,
        headers,
        getConversationId: getOrCreateConversationId,
        onError: showToast,
      });

    const [currentCollectionId, setCurrentCollectionId] = useState<string | undefined>(selectedCollectionId);
    const [currentRegion, setCurrentRegion] = useState<string | undefined>(selectedRegion);
    const [showFollowUp, setShowFollowUp] = useState(true);
    const selectedKnowledgeGroupRef = useRef<KnowledgeSourceGroup | null>(null);

    const pendingSearchRef = useRef<{
      searchQuery: string;
      conversationId: string;
      attributes: SearchQueryAttributes;
      secretAttributes?: string;
      allowedRegions?: string[];
      pageContext?: { type: 'text'; text: string }[];
      fileAttachments?: { type: AttachmentType; url: string; filename?: string }[];
    } | null>(null);

    const primaryTextareaRef = useRef<HTMLTextAreaElement>(null);
    const answerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const primarySearchRef = useRef<HTMLTextAreaElement>(null);
    const followUpSearchRef = useRef<HTMLTextAreaElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const currentReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

    // Use autocomplete hook if endpoint is provided
    const { suggestions: autocompleteSuggestions } = useAutocomplete({
      query,
      endpoint: autocompleteEndpoint || '',
      allowedRegions,
      collectionId: currentCollectionId,
      headers,
      conversationId: currentConversationId,
      delay: 300,
    });

    const { loadConversationById } = useConversationLoader({
      loadConversationEndpoint,
      headers,
      mergedTextConfig,
    });

    const {
      showHistory,
      setShowHistory,
      conversations,
      isLoadingHistory,
      hasUnreadConversations,
      isViewOnly,
      setIsViewOnly,
      handleSelectConversation,
      completeActiveConversations,
    } = useConversationHistory({
      enabled: isHistoryTabEnabled,
      conversationHistoryEndpoint,
      markConversationReadEndpoint,
      markConversationCompletedEndpoint,
      headers,
      userData: userData ?? undefined,
      currentConversationId,
      loadConversationById,
      reconnectHandoff,
      resetHandoff,
    });

    const { scrollToLastAnswer, scrollToAnswer } = useScrollManager(answerRefs, containerRef, bottomRef);
    const { subscribeToStateChanges } = useSubscriptionManager();
    const { includePageEnabled, includePageLoading, includePageDisabled, handleIncludePageToggle, fetchPageContext } =
      useIncludePage({ isAgentAssist, apiKey: apiKey ?? 'unknown', trackEvent });

    const loadFollowUpQuestions = async (
      searchQueryId: string,
      conversationId?: string,
    ): Promise<string[] | undefined> => {
      // Fetch follow-up questions if needed
      if (!followUpQuestionsEndpoint || disableFollowUpQuestions) {
        return;
      }

      try {
        const questions = await fetchFollowUpQuestions({
          endpoint: followUpQuestionsEndpoint,
          searchQueryId,
          headers,
          conversationId,
        });

        return questions;
      } catch (error) {
        console.error('Error fetching follow-up questions:', error);

        return undefined;
      }
    };

    const processAnswerStream = async ({
      reader,
      searchQueryId,
      conversationId,
      onFirstEvent,
    }: {
      reader: ReadableStreamDefaultReader<Uint8Array>;
      searchQueryId: string;
      conversationId?: string;
      onFirstEvent?: () => void;
    }) => {
      // Store the current reader so it can be cancelled
      currentReaderRef.current = reader;
      let accumulatedAnswer = '';
      try {
        let done = false,
          isFirstEvent = true,
          searchIntentId: string | undefined,
          isUncertain = false,
          value;

        while ((({ done, value } = await reader.read()), !done)) {
          // Check if reader was cancelled (set to null)
          if (currentReaderRef.current !== reader) {
            break;
          }
          if (isFirstEvent) {
            isFirstEvent = false;
            onFirstEvent?.();
          }

          const lines = new TextDecoder()
            .decode(value)
            .split(/\n\n/)
            .filter((line) => line.startsWith('data: '))
            .map((line) => line.replace(/^data: /, '').trim());

          for (const line of lines) {
            try {
              // End of stream response
              if (line === '[DONE]') {
                continue;
              }

              const event = JSON.parse(line) as AnswerStreamEvent;

              if (event.type === 'status-message') {
                answerListDispatch(
                  setStatusMessage({
                    message: event.message,
                  }),
                );
              }

              if (event.type === 'answer-text-chunk') {
                accumulatedAnswer += event.content;

                answerListDispatch(
                  appendAnswerChunk({
                    text: event.content,
                  }),
                );

                const uncertaintyCheck = isAnswerUncertain(accumulatedAnswer);
                isUncertain = uncertaintyCheck.uncertain;

                if (isUncertain) {
                  const uncertaintyText =
                    mergedTextConfig.uncertaintyFallbackText?.[uncertaintyCheck.language ?? 'en'] ??
                    accumulatedAnswer ??
                    "I don't know.";

                  answerListDispatch(markAsUncertain({ uncertaintyText }));
                }
              }

              if (event.type === 'answer-error') {
                answerListDispatch(
                  setAnswerError({
                    error: mergedTextConfig.errorText || event.message,
                  }),
                );
              }

              if (event.type === 'action-buttons') {
                searchIntentId = event.searchIntentId;
                answerListDispatch(
                  addActionButtons({
                    searchIntentId,
                    actions: event.actions,
                  }),
                );
              }

              if (event.type === 'get-action-inputs') {
                searchIntentId = event.searchIntentId;

                answerListDispatch(
                  getActionInputs({
                    searchIntentId: event.searchIntentId,
                    inputs: event.inputs,
                    actionId: event.actionId,
                    schema: event.schema,
                    initialValues: event.values,
                    onSubmit: async (parameters) => {
                      if (event.actionType === 'client_execution') {
                        const results = await executeClientAction(event.actionKey, parameters);

                        return generateAnswerForClientAction({
                          searchQueryId,
                          conversationId,
                          searchIntentId: event.searchIntentId,
                          actionId: event.actionId,
                          results,
                        });
                      }

                      if (event.actionType === 'run_workflow') {
                        answerListDispatch(invokeAction());
                        answerListDispatch(
                          setStatusMessage({
                            message: mergedTextConfig.loadingAnswerText!,
                          }),
                        );

                        return generateAnswerForQuery({
                          searchQueryId,
                          conversationId,
                          parameters,
                        });
                      }

                      return generateAnswerForAction({
                        searchQueryId,
                        conversationId,
                        searchIntentId: event.searchIntentId,
                        actionId: event.actionId,
                        parameters,
                      });
                    },
                  }),
                );
              }

              // Citations (or sources) happens last in the event
              // where the response is coming from agentic flow
              if (event.type === 'citations') {
                answerListDispatch(
                  setSearchResults({
                    searchQueryId,
                    results: event.data,
                    state: 'completed',
                  }),
                );
              }
            } catch {
              console.warn('Failed to parse answer stream block');
            }
          }
        }

        return { answer: accumulatedAnswer, searchIntentId, isUncertain };
      } catch (error) {
        // If reader was cancelled, don't throw error
        if (currentReaderRef.current !== reader) {
          return { answer: '', searchIntentId: undefined, isUncertain: false };
        }
        throw error;
      } finally {
        // Only release lock if this is still the current reader
        if (currentReaderRef.current === reader) {
          currentReaderRef.current = null;
          reader.releaseLock();
        }
      }
    };

    const generateAnswerForQuery = async ({
      searchQueryId,
      conversationId,
      context,
      attachments,
      parameters,
    }: {
      searchQueryId: string;
      conversationId?: string;
      context?: { type: 'text'; text: string }[];
      attachments?: { type: AttachmentType; url: string; filename?: string }[];
      parameters?: ActionInputs;
    }) => {
      try {
        const followUpQuestions = loadFollowUpQuestions(searchQueryId);

        const response = await fetchAnswerStream({
          endpoint: answerStreamEndpoint,
          headers,
          body: {
            type: 'generate-for-query',
            conversationId,
            searchQueryId,
            profileId: userData?.userId as string | undefined,
            ...(context && { context }),
            ...(attachments?.length && { attachments }),
            ...(parameters && { parameters }),
          },
        });

        const reader = response.body?.getReader();
        if (!reader) return;

        const { answer, searchIntentId, isUncertain } = await processAnswerStream({
          reader,
          searchQueryId,
          conversationId,
          onFirstEvent: () => {
            trackEvent?.('First Streamed Answer Chunk Received', { conversationId, searchQueryId });
          },
        });

        if (!answer) return;

        // load follow-up questions if no search intent id is returned - i.e. no search intent was matched
        void followUpQuestions.then((questions) => {
          if (questions && questions.length > 0 && !searchIntentId) {
            answerListDispatch(setFollowUpQuestions({ questions }));
          }
        });

        if (isUncertain) {
          trackEvent?.('Answer Not Found', { conversationId, searchQueryId });
        } else {
          trackEvent?.('Answer Found', { conversationId, searchQueryId });
        }
      } catch (e) {
        console.error('Error generating answer for query:', e);
        answerListDispatch(
          setAnswerError({
            error:
              mergedTextConfig.errorText ||
              'Sorry, we were unable to process the request right now. Please try again in a moment.',
          }),
        );
      } finally {
        answerListDispatch(completeAnswer());
      }
    };

    const generateAnswerForAction = async ({
      searchQueryId,
      searchIntentId,
      conversationId,
      actionId,
      parameters,
    }: {
      searchQueryId: string;
      conversationId?: string;
      searchIntentId: string;
      actionId: string;
      parameters: ActionInputs;
    }) => {
      answerListDispatch(invokeAction());

      try {
        const response = await fetchAnswerStream({
          endpoint: answerStreamEndpoint,
          headers,
          body: {
            type: 'invoke-action',
            conversationId,
            searchQueryId,
            searchIntentId,
            actionId,
            parameters,
            profileId: userData?.userId as string | undefined,
          },
        });

        const reader = response.body?.getReader();
        if (!reader) return;

        await processAnswerStream({
          reader,
          searchQueryId,
        });
      } catch (e) {
        console.error('Error generating answer for action:', e);
        answerListDispatch(
          setAnswerError({
            error:
              mergedTextConfig.errorText ||
              'Sorry, we were unable to process the request right now. Please try again in a moment.',
          }),
        );
      } finally {
        answerListDispatch(completeAnswer());
        setIsSearching(false);
      }
    };

    const generateAnswerForClientAction = async ({
      searchQueryId,
      searchIntentId,
      conversationId,
      actionId,
      results,
    }: {
      searchQueryId: string;
      conversationId?: string;
      searchIntentId: string;
      actionId: string;
      results: ClientExecutionResult;
    }) => {
      answerListDispatch(invokeAction());

      try {
        const response = await fetchAnswerStream({
          endpoint: answerStreamEndpoint,
          headers,
          body: {
            type: 'client-execution-result',
            conversationId,
            searchQueryId,
            searchIntentId,
            actionId,
            results,
            profileId: userData?.userId as string | undefined,
          },
        });

        const reader = response.body?.getReader();
        if (!reader) return;

        await processAnswerStream({
          reader,
          searchQueryId,
        });
      } catch (e) {
        console.error('Error generating answer for action:', e);
        answerListDispatch(
          setAnswerError({
            error:
              mergedTextConfig.errorText ||
              'Sorry, we were unable to process the request right now. Please try again in a moment.',
          }),
        );
      } finally {
        answerListDispatch(completeAnswer());
        setIsSearching(false);
      }
    };

    // Function to handle search API call
    const handleSearchApiCall = async ({
      searchQuery,
      conversationId,
      attributes,
      secretAttributes,
      allowedRegions,
      knowledgeGroupCollectionIds,
    }: {
      searchQuery: string;
      conversationId?: string;
      attributes?: SearchQueryAttributes;
      secretAttributes?: string;
      allowedRegions?: string[];
      knowledgeGroupCollectionIds?: string[];
    }) => {
      const collectionId = knowledgeGroupCollectionIds ?? currentCollectionId;

      const searchResponse = await searchApi({
        endpoint: searchEndpoint,
        query: searchQuery,
        collectionId,
        headers,
        conversationId,
        attributes,
        secretAttributes,
        allowedRegions,
        source: isAgentAssist ? 'agent_assist' : undefined,
      });

      if (searchResponse.results.length === 0 && !searchResponse.isAgenticResponse) {
        trackEvent?.('No Articles Found', { conversationId, searchQuery });

        return;
      }

      answerListDispatch(
        setSearchResults({
          searchQueryId: searchResponse.searchQueryId,
          results: searchResponse.results,
        }),
      );

      return searchResponse.searchQueryId;
    };

    interface PerformSearchProps {
      searchQuery: string;
      event?: { name: string; metaData?: Record<string, any> };
    }
    // Main function to perform the search
    const performSearch = async ({ searchQuery = query.trim(), event }: PerformSearchProps) => {
      if (!searchQuery) return;

      const conversationId = currentConversationId || createConversationId();
      setCurrentConversationId(conversationId);

      // Collect uploaded attachments and clear them before dispatching
      const fileAttachments = getUploadedAttachments();
      clearAttachedFiles();

      setIsSearching(true);
      setShowResults(true);
      setActiveAnswerIndex(answers.length);
      scrollToLastAnswer();

      answerListDispatch(
        appendNewAnswer({
          query: searchQuery,
          attachments: fileAttachments,
        }),
      );

      // ensure the UI updates before the search API call
      await Promise.resolve();

      // focus on follow-up search field if it's visible
      followUpSearchRef.current?.focus();

      // Fetch page content if toggle is enabled
      const pageContext = await fetchPageContext(conversationId, searchQuery);

      // If knowledge groups exist and none is selected yet, pause for user to pick
      const needsGroupSelection =
        knowledgeSourceGroups && knowledgeSourceGroups.length > 0 && !selectedKnowledgeGroupRef.current;
      if (needsGroupSelection) {
        pendingSearchRef.current = {
          searchQuery,
          conversationId,
          attributes: { ...userData } as SearchQueryAttributes,
          secretAttributes,
          allowedRegions,
          pageContext: pageContext ?? undefined,
          fileAttachments: fileAttachments.length > 0 ? fileAttachments : undefined,
        };
        answerListDispatch(setAwaitingKnowledgeGroup());
        setIsSearching(false);

        return;
      }

      try {
        const attributes = { ...userData } as SearchQueryAttributes;
        const knowledgeGroupCollectionIds = selectedKnowledgeGroupRef.current?.collectionIds;
        const searchQueryId = await handleSearchApiCall({
          searchQuery,
          conversationId,
          attributes,
          secretAttributes,
          allowedRegions,
          knowledgeGroupCollectionIds,
        });

        if (searchQueryId) {
          event &&
            trackEvent?.(event.name, {
              conversationId,
              searchQuery,
              searchQueryId,
              ...event.metaData,
            });

          await generateAnswerForQuery({
            searchQueryId,
            conversationId,
            context: pageContext,
            attachments: fileAttachments.length > 0 ? fileAttachments : undefined,
          });
        } else {
          answerListDispatch(noArticlesFound());
        }
      } catch (error) {
        console.error('Error generating answer:', error);

        // track the error event
        event &&
          trackEvent?.(`${event.name} - Search Error`, {
            conversationId,
            searchQuery,
            ...event.metaData,
          });

        answerListDispatch(
          setAnswerError({
            error:
              mergedTextConfig.errorText ||
              'Sorry, we were unable to process the request right now. Please try again in a moment.',
          }),
        );
      } finally {
        setIsSearching(false);
      }
    };

    const handleKnowledgeGroupSelect = async (group: KnowledgeSourceGroup) => {
      selectedKnowledgeGroupRef.current = group;

      const pending = pendingSearchRef.current;
      if (!pending) return;
      pendingSearchRef.current = null;

      answerListDispatch(setKnowledgeGroupSelected());

      setIsSearching(true);
      try {
        const searchQueryId = await handleSearchApiCall({
          searchQuery: pending.searchQuery,
          conversationId: pending.conversationId,
          attributes: pending.attributes,
          secretAttributes: pending.secretAttributes,
          allowedRegions: pending.allowedRegions,
          knowledgeGroupCollectionIds: group.collectionIds,
        });

        if (searchQueryId) {
          trackEvent?.('Knowledge Group Selected', {
            conversationId: pending.conversationId,
            searchQuery: pending.searchQuery,
            searchQueryId,
            knowledgeGroupId: group.id,
            knowledgeGroupName: group.name,
          });

          await generateAnswerForQuery({
            searchQueryId,
            conversationId: pending.conversationId,
            context: pending.pageContext,
            attachments: pending.fileAttachments,
          });
        } else {
          answerListDispatch(noArticlesFound());
        }
      } catch (error) {
        console.error('Error generating answer after group selection:', error);
        answerListDispatch(
          setAnswerError({
            error:
              mergedTextConfig.errorText ||
              'Sorry, we were unable to process the request right now. Please try again in a moment.',
          }),
        );
      } finally {
        setIsSearching(false);
      }
    };

    const createNewThread = (firstQuery?: string) => {
      completeActiveConversations();
      setIsViewOnly(false);
      resetHandoff?.();

      // Cancel any ongoing stream
      if (currentReaderRef.current) {
        const reader = currentReaderRef.current;
        currentReaderRef.current = null;
        cancelStreamReader(reader);
      }

      // Reset loading state
      setIsSearching(false);

      // Clear conversation state first (always)
      setQuery('');
      setFollowUpQuery('');
      setShowResults(false);
      answerListDispatch(clearAll());
      setActiveAnswerIndex(0);
      clearAttachedFiles();
      clearCurrentConversationId();
      selectedKnowledgeGroupRef.current = null;
      pendingSearchRef.current = null;

      trackEvent?.('New Thread Created', { oldConversationId: currentConversationId });

      if (primaryTextareaRef.current) {
        primaryTextareaRef.current.style.height = 'auto';
      }

      // If firstQuery is provided, set it and trigger search
      if (firstQuery) {
        setQuery(firstQuery);
        void performSearch({ searchQuery: firstQuery, event: { name: 'New Thread Created with Query' } });
      }
    };

    // Expose the methods via ref
    useImperativeHandle(ref, () => ({
      createNewThread,
      showFollowUpThread: (show: boolean) => {
        setShowFollowUp(show);
      },
      subscribeToStateChanges,
      focusSearchField: () => primarySearchRef.current?.focus() || followUpSearchRef.current?.focus(),
      showCloseButton: (showButton: boolean = true) => {
        setShowMobileCloseWidgetButton(showButton);
      },
      loadConversation: loadConversationById,
    }));

    // Update currentCollectionId when selectedCollectionId prop changes
    useEffect(() => {
      setCurrentCollectionId(selectedCollectionId);
    }, [selectedCollectionId]);

    // Update currentRegion when selectedRegion prop changes
    useEffect(() => {
      setCurrentRegion(selectedRegion);
    }, [selectedRegion]);

    const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    // Function to initiate a primary search
    const handlePrimarySearch = async (searchQuery: string = query.trim()) => {
      await performSearch({ searchQuery, event: { name: 'Primary Field Search Submitted' } });
    };

    const handleFeedback = async (response: FeedbackResponse, searchQueryId: string, answerIndex: number) => {
      if (!feedbackEndpoint) return;

      // Update UI state immediately
      answerListDispatch(
        setUserFeedback({
          feedback: response,
          index: answerIndex,
        }),
      );

      try {
        await sendFeedback({
          endpoint: feedbackEndpoint,
          searchQueryId,
          response,
          headers,
          conversationId: currentConversationId,
        });

        trackEvent?.(response === 'accept' ? 'Answer Accepted' : 'Answer Rejected', {
          conversationId: currentConversationId,
          searchQueryId,
        });
      } catch (error) {
        console.error('Error sending feedback:', error);
        // Revert UI state on error
        answerListDispatch(
          clearUserFeedback({
            index: answerIndex,
          }),
        );
      }
    };

    const handleFeedbackReasonSubmit = async (reason: string, searchQueryId: string) => {
      if (!feedbackReasonEndpoint) return;

      try {
        await sendFeedbackReason({
          endpoint: feedbackReasonEndpoint,
          searchQueryId,
          reason,
          headers,
        });
      } catch (error) {
        console.error('Error sending feedback reason:', error);
      }
    };

    const handleFollowUpSearch = async (customQuery?: string) => {
      // Use customQuery if provided, otherwise use the state value
      const queryToUse = customQuery !== undefined ? customQuery : followUpQuery;

      if (queryToUse.trim()) {
        setQuery(queryToUse);
        setFollowUpQuery('');
        await performSearch({ searchQuery: queryToUse, event: { name: 'Follow-up Search Field Submitted' } });
      }
    };

    const handleSuggestedQuestionClick = (suggestion: Suggestion) => {
      if (suggestion.conversationId) {
        trackEvent?.('Recent Conversation Clicked', {
          conversationId: currentConversationId,
          question: suggestion.question,
        });
        loadConversationById(suggestion.conversationId);

        return;
      }

      void performSearch({ searchQuery: suggestion.question, event: { name: 'Suggested Question Clicked' } });
    };

    const handleAutocompleteSuggestionClick = (suggestion: Suggestion) => {
      // autocomplete suggestions don't have conversation ids
      void performSearch({ searchQuery: suggestion.question, event: { name: 'Autocomplete Suggestion Clicked' } });
    };

    const handleCollectionChange = (collectionId: string) => {
      setCurrentCollectionId(collectionId);
      onCollectionChange?.(collectionId);
    };

    const handleRegionChange = (regionId: string) => {
      setCurrentRegion(regionId);
      onRegionChange?.(regionId);
      // Notify parent component to refetch config with new region
      trackEvent?.('Region Changed', { region: regionId, conversationId: currentConversationId });
    };

    // Listen for URL changes and reset state when conversation param is removed
    useEffect(() => {
      const handleUrlChange = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlConversationId = urlParams.get('conversation');

        if (!urlConversationId) {
          setQuery('');
          setFollowUpQuery('');
          setShowResults(false);
          answerListDispatch(clearAll());
          setActiveAnswerIndex(0);
          clearAttachedFiles();
          if (primaryTextareaRef.current) {
            primaryTextareaRef.current.style.height = 'auto';
          }
        }
      };

      window.addEventListener('popstate', handleUrlChange);

      return () => window.removeEventListener('popstate', handleUrlChange);
    }, []);

    // Update the follow-up question click handler to use performSearch
    const handleFollowUpQuestionClick = (question: string) => {
      trackEvent?.('Follow-up Question Clicked', { conversationId: currentConversationId, question });
      void performSearch({ searchQuery: question, event: { name: 'Follow-up Question Clicked' } });
    };

    // handle the next best action click
    const handleNextBestActionClick = ({ action, searchQueryId, query, answer }: ActionClick) => {
      trackEvent?.('Next Best Action Clicked', {
        conversationId: currentConversationId,
        searchQueryId,
        action: action.label,
      });
      const conversation = buildConversationContext(answers, currentConversationId);
      onNextBestActionClick?.({ action, searchQueryId, query, answer, conversation });
    };

    // Notify parent window about height changes (autocomplete, streaming, etc.)
    useEffect(() => {
      const container = containerRef.current;
      if (!container || !isSearchWidget) return;

      let lastSentHeight = 0;
      let rafId: number | null = null;

      const calculateTotalHeight = (): number => {
        const innerContent = container.firstElementChild as HTMLElement;
        const contentHeight = innerContent?.scrollHeight ?? container.scrollHeight;

        const footerHeight = document.querySelector('[data-name="BrandFooter"]')?.clientHeight ?? 0;
        const headerHeight = document.querySelector('[data-name="Header"]')?.clientHeight ?? 0;
        const followUpHeight = showResults && showFollowUp ? 70 : 0;
        const chatSearchHeight = document.querySelector('[data-name="ChatSearch"]')?.clientHeight ?? 0;
        const verticalPadding = 32;

        const totalHeight = contentHeight + footerHeight + followUpHeight + headerHeight + verticalPadding;

        if (!showResults) {
          return totalHeight;
        }

        return Math.max(totalHeight, chatSearchHeight);
      };

      const sendResize = () => {
        const totalHeight = calculateTotalHeight();

        if (totalHeight > 0 && totalHeight !== lastSentHeight) {
          lastSentHeight = totalHeight;
          window.parent.postMessage(
            {
              type: 'RESIZE_IFRAME',
              height: totalHeight,
              widgetKey: apiKey,
            },
            '*',
          );
        }
      };

      const handleResize = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          sendResize();
          rafId = null;
        });
      };

      // Initial resize with small delay for ScrollArea to initialize
      const timeoutId = setTimeout(sendResize, 100);

      // Observe content changes (including streaming answers)
      const resizeObserver = new ResizeObserver(handleResize);

      // Observe both container and inner content if available
      const innerContent = container.firstElementChild;
      if (innerContent) {
        resizeObserver.observe(innerContent);
      }
      resizeObserver.observe(container);

      return () => {
        clearTimeout(timeoutId);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        resizeObserver.disconnect();
      };
    }, [isSearchWidget, apiKey, showResults, showFollowUp]);

    return (
      <div
        className={cn(
          'w-full relative h-dvh flex flex-col justify-between @container',
          'md:h-auto md:p-4 md:bg-dark-100',
          className,
        )}
        data-name="ChatSearch"
        style={style}
      >
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />

        {/* Always show the header when in widget mode, or when there are no results */}
        {(isWidgetMode || !showResults) && (
          <Header
            showHistoryButton={isHistoryTabEnabled}
            isSearchWidget={isSearchWidget}
            data-name="Header"
            headerText={mergedTextConfig.headerText!}
            onToggleSizeButtonClick={() => {
              onToggleSizeButtonClick?.();
            }}
            onBackButtonClick={() => void createNewThread()}
            onCloseWidgetClick={() => onCloseWidget?.()}
            showHistory={showHistory}
            hasUnreadConversations={hasUnreadConversations}
            onHistoryButtonClick={() => setShowHistory(true)}
            onHistoryBackClick={() => setShowHistory(false)}
            className={cn('@3xl:mb-4', {
              '!block': showResults && isSearchWidget,
            })}
          />
        )}
        <ScrollArea
          className="w-full relative h-auto flex-grow [&>[data-radix-scroll-area-viewport]>div:first-child]:!block"
          ref={containerRef}
        >
          <AnimatePresence>
            {showHistory ? (
              <HistoryPage
                conversations={conversations}
                isLoading={isLoadingHistory}
                onSelectConversation={handleSelectConversation}
              />
            ) : isLoadingConversation ? (
              <LoadingConversation />
            ) : !showResults ? (
              <>
                {/* Finished loading conversation, no results yet, show search experience */}
                <motion.div
                  key="search"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="w-full md:space-y-4"
                >
                  {/* Primary Search */}
                  <div
                    className={cn(
                      'pb-4',
                      isSearchWidget ? 'bg-transparent p-3 md:p-0' : 'px-3 bg-primary md:p-0 md:bg-transparent',
                    )}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                    >
                      <PrimarySearch
                        ref={primarySearchRef}
                        query={query}
                        onQueryChange={(value) => {
                          setQuery(value);
                          if (primaryTextareaRef.current) {
                            adjustTextareaHeight(primaryTextareaRef.current);
                          }
                        }}
                        onSearch={() => void handlePrimarySearch()}
                        textConfig={mergedTextConfig}
                        collections={collections}
                        currentCollectionId={currentCollectionId}
                        onCollectionChange={handleCollectionChange}
                        allowedRegions={allowedRegions}
                        currentRegion={currentRegion}
                        onRegionChange={handleRegionChange}
                        apiKey={apiKey}
                        fetchRegionConfig={fetchRegionConfig}
                        enableFileAttachment={enableFileAttachment}
                        attachedFiles={attachedFiles}
                        isUploading={isUploading}
                        onAddFiles={addFiles}
                        onRemoveFile={removeFile}
                        isSearching={isSearching}
                        autocompleteSuggestions={autocompleteSuggestions}
                        onAutocompleteSuggestionSelect={handleAutocompleteSuggestionClick}
                        isAgentAssist={isAgentAssist}
                        includePageEnabled={includePageEnabled}
                        includePageLoading={includePageLoading}
                        includePageDisabled={includePageDisabled}
                        onIncludePageToggle={handleIncludePageToggle}
                      />
                    </motion.div>
                  </div>

                  {/* Suggestions */}
                  <Suggestions
                    suggestions={initialSuggestions}
                    onQuestionClick={handleSuggestedQuestionClick}
                    title={mergedTextConfig.suggestionsTitle}
                  />

                  {/* Body Action Buttons */}
                  {isWidgetMode && bodyActionButtons && bodyActionButtons.length > 0 && (
                    <div className="mt-4 mb-4 px-3 @3xl:px-0">
                      <div className="space-y-3">
                        <NextBestActions
                          nextBestActions={bodyActionButtons || []}
                          onActionClick={(action) =>
                            handleNextBestActionClick({
                              action,
                              searchQueryId: '',
                              query: '',
                              answer: '',
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              </>
            ) : (
              <>
                {/* We now have answers, so we can show the results */}
                <motion.div
                  key="results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="w-full max-w-[100vw] space-y-4 p-3 md:p-0"
                >
                  {/* Answers */}
                  {answers.map((answer, i) => (
                    <motion.div
                      key={i}
                      ref={(el) => (answerRefs.current[i] = el)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="space-y-4 last:!mb-8"
                    >
                      <Answer
                        index={i}
                        blocks={answer.blocks}
                        question={answer.question}
                        attachments={answer.attachments}
                        state={answer.state}
                        searchResults={answer.searchResults}
                        searchQueryId={answer.searchQueryId}
                        searchIntentId={answer.searchIntentId}
                        hideCitations={hideCitations}
                        feedback={answer.feedback}
                        onFeedback={(response) => void handleFeedback(response, answer.searchQueryId!, i)}
                        disableFeedback={disableFeedback}
                        textConfig={mergedTextConfig}
                        redirectRules={redirectRules}
                        nextBestActions={nextBestActions}
                        actions={answer.actions}
                        handleActionClick={handleNextBestActionClick}
                        isLastAnswer={i === answers.length - 1}
                        onFeedbackReasonSubmit={(reason) =>
                          void handleFeedbackReasonSubmit(reason, answer.searchQueryId!)
                        }
                        hasError={answer.hasError}
                        statusMessage={answer.statusMessage}
                        knowledgeSourceGroups={knowledgeSourceGroups}
                        onKnowledgeGroupSelect={(group) => void handleKnowledgeGroupSelect(group)}
                      />
                      {i === answers.length - 1 &&
                        !disableFollowUpQuestions &&
                        answer.followUpQuestions &&
                        answer.state === 'completed' &&
                        !answer.hasError && (
                          <FollowUpQuestions
                            questions={answer.followUpQuestions}
                            onQuestionClick={handleFollowUpQuestionClick}
                          />
                        )}
                    </motion.div>
                  ))}
                </motion.div>
              </>
            )}
          </AnimatePresence>
          {showResults && answers.length > 1 && (
            <div className="hidden lg:block">
              <TimelineNavigation
                questions={answers.map((a) => a.question)}
                activeIndex={activeAnswerIndex}
                onNavigate={(index) => {
                  setActiveAnswerIndex(index);
                  scrollToAnswer(index);
                }}
                offset={offset}
              />
            </div>
          )}

          {disclaimer && isWidgetMode && !(showResults && showFollowUp) && (
            <div
              data-name="disclaimer"
              className={cn(
                'mt-2 px-3 @3xl:px-0 text-xs text-dark-500 flex items-center gap-1 justify-center text-center w-full md:static md:mt-4 md:text-left md:flex md:gap-4 leading-5',
                isSearchWidget ? 'static mb-2 md:text-left [&>svg]:flex-shrink-0' : 'absolute bottom-12',
              )}
            >
              <MemoizedIcon iconName="Info" className="size-4" />
              <MemoizedReactMarkdown
                className="[&_a]:underline [&_a:hover]:no-underline"
                remarkPlugins={markdownRemarkPlugins}
                rehypePlugins={markdownRehypePlugins}
                components={markdownComponents}
              >
                {disclaimer}
              </MemoizedReactMarkdown>
            </div>
          )}

          {conversationInlineSlot != null && showResults && !showHistory && (
            <div className="-mt-6 w-full max-w-[100vw] px-3 pb-0 pt-0 text-foreground md:px-0">
              {conversationInlineSlot}
            </div>
          )}

          {/* Invisible div to scroll to */}
          <div ref={bottomRef} />
        </ScrollArea>

        {showResults &&
          showFollowUp &&
          !isViewOnly &&
          !liveHumanAgentChatSlot &&
          !showHistory &&
          (() => {
            const isAwaitingGroup = answers.some((a) => a.state === 'awaiting-knowledge-group');

            return (
              <FollowUpSearchBar
                ref={followUpSearchRef}
                initialQuery={followUpQuery}
                onQueryChange={setFollowUpQuery}
                isSearching={isSearching}
                handleFollowUpSearch={handleFollowUpSearch}
                textConfig={mergedTextConfig}
                enableFileAttachment={enableFileAttachment}
                attachedFiles={attachedFiles}
                isUploading={isUploading}
                onAddFiles={addFiles}
                onRemoveFile={removeFile}
                disabled={isAwaitingGroup}
              />
            );
          })()}

        {showResults && isViewOnly && !liveHumanAgentChatSlot && !showHistory && (
          <ResolvedConversationFooter onStartNewConversation={() => createNewThread()} />
        )}

        {!showHistory && liveHumanAgentChatSlot}
      </div>
    );
  },
);

ChatSearchComponent.displayName = 'ChatSearch';

const ChatSearch = forwardRef<ChatSearchHandle, ChatSearchProps>(({ featureFlags, ...props }, ref) => (
  <FeatureFlagProvider flags={featureFlags}>
    <ChatSearchProvider
      searchPath={props.searchPath}
      contentTypeEndpoint={props.contentTypeEndpoint}
      widgetKey={props.apiKey}
    >
      <ChatSearchComponent {...props} ref={ref} />
    </ChatSearchProvider>
  </FeatureFlagProvider>
));

ChatSearch.displayName = 'ChatSearch';

export default ChatSearch;
